/**
 * The three widths an address can take, widest first:
 *
 * - `full`: the whole address, e.g. `9HjDWerzphieQHH36D9BWuEUKaQXyXAwANw8MhFKGjvj`;
 * - `middle`: 16 characters, 8 + "…" + 8, e.g. `9HjDWerz…8MhFKGjvj`;
 * - `short`: 8 characters, 4 + "…" + 4, e.g. `9HjD…Gjvj`.
 *
 * Pure: no DOM. The component and hook measure; this decides.
 */
export type AddressTier = "full" | "middle" | "short";

export const ADDRESS_TIERS: readonly AddressTier[] = ["full", "middle", "short"];

const ELLIPSIS = "…";

function middleOut(address: string, keep: number): string {
    // Nothing to save: the shortened form would be as long as the address.
    if (address.length <= keep * 2 + 1) return address;
    return `${address.slice(0, keep)}${ELLIPSIS}${address.slice(-keep)}`;
}

/** The address as it reads at `tier`. */
export function formatAddressTier(address: string, tier: AddressTier): string {
    if (tier === "middle") return middleOut(address, 8);
    if (tier === "short") return middleOut(address, 4);
    return address;
}

export interface PickAddressTierOptions {
    /** Text drawn before the address in the same box (e.g. a placeholder lead). */
    prefix?: string;
    /** Text drawn after the address in the same box. */
    suffix?: string;
}

/**
 * The widest tier whose text fits `available` pixels, measured by `measure`.
 * `short` is the floor: it is returned even when it does not fit, since
 * shortening further stops reading as an address.
 *
 * An unmeasurable box (`available <= 0`, e.g. not laid out yet or hidden)
 * gets `full`, so nothing is shortened before there is a reason to.
 */
export function pickAddressTier(
    address: string,
    available: number,
    measure: (text: string) => number,
    { prefix = "", suffix = "" }: PickAddressTierOptions = {}
): AddressTier {
    if (!address || !(available > 0)) return "full";
    for (const tier of ADDRESS_TIERS) {
        if (tier === "short") return tier;
        const text = `${prefix}${formatAddressTier(address, tier)}${suffix}`;
        if (measure(text) <= available) return tier;
    }
    return "short";
}
